import type { ReactNode } from 'react'
import { cn } from '@/components/ui'

/**
 * Deux pastilles de la DA 2027.
 *
 * - **`Info`** : une information chiffrée (« 5 sur 6 feuilles signées »), au
 *   trait — blanc, contour prune, découpe crème. Le chiffre en gras dans
 *   `valeur`, le reste en `children`.
 * - **`Sticker`** : un mot ou deux (« J-3 », « Live. », « New »), posé sur un
 *   bord ou un coin. Trois au plus par visuel, jamais sur un titre ni sur le
 *   logo. Majuscule en début de mot (décision de Baptiste).
 *
 * L'inclinaison alterne d'une pastille à l'autre : c'est l'appelant qui la
 * choisit (`incline`), pour que deux voisines ne penchent pas pareil.
 */

type Incline = 'gauche' | 'droite'

const INCLINE: Record<Incline, string> = {
  gauche: '-rotate-2',
  droite: 'rotate-2',
}

export function Info({
  valeur,
  children,
  incline = 'gauche',
  className,
}: {
  valeur?: ReactNode
  children: ReactNode
  incline?: Incline
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1.5 rounded-pill bg-surface px-4 py-[9px] text-[14px] font-semibold whitespace-nowrap text-ink',
        'shadow-[inset_0_0_0_2px_var(--ink),0_0_0_4px_var(--creme),0_4px_8px_rgba(43,23,72,0.14)]',
        INCLINE[incline],
        className,
      )}
    >
      {valeur !== undefined && <b className="font-black tracking-[-0.03em]">{valeur}</b>}
      {children}
    </span>
  )
}

const STICKER: Record<'lavande' | 'rose', string> = {
  lavande: 'bg-lavender',
  rose: 'bg-rose',
}

export function Sticker({
  children,
  couleur = 'lavande',
  incline = 'gauche',
  className,
}: {
  children: ReactNode
  /** Jamais rose sur un aplat rose : il disparaîtrait. */
  couleur?: 'lavande' | 'rose'
  incline?: Incline
  /** Le placement (`absolute`, coin) est à l'appelant. */
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill px-[18px] py-[9px] text-[16px] font-extrabold tracking-[-0.02em] whitespace-nowrap text-ink',
        'shadow-[0_0_0_5px_var(--creme),0_7px_9px_rgba(20,8,40,0.22)]',
        STICKER[couleur],
        incline === 'gauche' ? '-rotate-6' : 'rotate-6',
        className,
      )}
    >
      {children}
    </span>
  )
}
